import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import { getProduct, createProduct, updateProduct, resolveImages, getLeafCategories } from '../../api';
import ImageUploader from '../../components/admin/ImageUploader';
import TagInput from '../../components/admin/TagInput';
import { useToast } from '../../components/Toast';
import Loader from '../../components/Loader';

const blank = {
  name: '', shortDescription: '', description: '', category: '', categoryId: '',
  price: '', mrp: '', featured: false, inStock: true, showPrice: true, showColors: true,
  images: [], tags: [], colors: [],
};

export default function ProductForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { toast, ToastHost } = useToast();
  const [form, setForm] = useState(blank);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getLeafCategories().then(setLeaves).catch(() => setLeaves([]));
  }, []);

  useEffect(() => {
    if (!editing) return;
    getProduct(id)
      .then((p) => setForm({
        name: p.name, shortDescription: p.shortDescription || '', description: p.description || '',
        category: p.category || '', categoryId: p.categoryId || '',
        price: p.price ?? '', mrp: p.mrp ?? '',
        featured: !!p.featured, inStock: p.inStock !== false,
        showPrice: p.showPrice !== false, showColors: p.showColors !== false,
        images: p.images || [], tags: p.tags || [],
        colors: (p.colors || []).map((c) => ({ ...c, images: c.images || [] })),
      }))
      .catch(() => toast('Failed to load product', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Colours
  const addColor = () => set('colors', [...form.colors, { name: '', hex: '#7c5cff', images: [] }]);
  const setColor = (i, key, val) => set('colors', form.colors.map((c, idx) => idx === i ? { ...c, [key]: val } : c));
  const removeColor = (i) => set('colors', form.colors.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast('Name is required', 'error');
    setSaving(true);
    try {
      // Upload any new files (general + per-colour) and swap to urls.
      const images = await resolveImages(form.images);
      const colors = [];
      for (const c of form.colors) {
        colors.push({ name: c.name, hex: c.hex, images: await resolveImages(c.images) });
      }
      const leaf = leaves.find((l) => l._id === form.categoryId);
      const payload = {
        name: form.name,
        shortDescription: form.shortDescription,
        description: form.description,
        categoryId: form.categoryId || '',
        category: leaf ? leaf.name : (form.category || 'General'),
        price: Number(form.price) || 0,
        mrp: Number(form.mrp) || 0,
        featured: form.featured,
        inStock: form.inStock,
        showPrice: form.showPrice,
        showColors: form.showColors,
        images,
        tags: form.tags,
        colors,
      };
      if (editing) await updateProduct(id, payload);
      else await createProduct(payload);
      toast('Product saved');
      setTimeout(() => navigate('/admin/products'), 700);
    } catch (err) {
      toast(err.response?.data?.message || 'Save failed', 'error');
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={submit}>
      <div className="acontent__head">
        <h1>{editing ? 'Edit' : 'New'} Product</h1>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/products')}><FiArrowLeft /> Back</button>
      </div>

      <div className="acard">
        <h3>Basics</h3>
        <div className="form-grid">
          <div className="field full">
            <label>Name <span className="req">*</span></label>
            <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Product name" />
          </div>
          <div className="field">
            <label>Category</label>
            <select className="select" value={form.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
              <option value="">— Select a category —</option>
              {leaves.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
            </select>
            {leaves.length === 0 && <small className="muted">No leaf categories yet. Create categories first.</small>}
          </div>
          <div className="field">
            <label>Price (₹)</label>
            <input className="input" type="number" value={form.price} onChange={(e) => set('price', e.target.value)} />
          </div>
          <div className="field">
            <label>MRP (₹)</label>
            <input className="input" type="number" value={form.mrp} onChange={(e) => set('mrp', e.target.value)} />
          </div>
          <div className="field">
            <label>Short description</label>
            <input className="input" value={form.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} placeholder="One-liner" />
          </div>
          <div className="field full">
            <label>Full description</label>
            <textarea className="textarea" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Detailed description…" />
          </div>
          <div className="field full">
            <label>Tags</label>
            <TagInput value={form.tags} onChange={(v) => set('tags', v)} />
          </div>
          <div className="field flex center gap">
            <button type="button" className={`toggle ${form.featured ? 'on' : ''}`} onClick={() => set('featured', !form.featured)} />
            <label style={{ margin: 0 }}>Featured</label>
          </div>
          <div className="field flex center gap">
            <button type="button" className={`toggle ${form.inStock ? 'on' : ''}`} onClick={() => set('inStock', !form.inStock)} />
            <label style={{ margin: 0 }}>In stock</label>
          </div>
          <div className="field flex center gap">
            <button type="button" className={`toggle ${form.showPrice ? 'on' : ''}`} onClick={() => set('showPrice', !form.showPrice)} />
            <label style={{ margin: 0 }}>Show price on website</label>
          </div>
          <div className="field flex center gap">
            <button type="button" className={`toggle ${form.showColors ? 'on' : ''}`} onClick={() => set('showColors', !form.showColors)} />
            <label style={{ margin: 0 }}>Show colours on website</label>
          </div>
        </div>
      </div>

      <div className="acard">
        <h3>Gallery images</h3>
        <ImageUploader value={form.images} onChange={(v) => set('images', v)} label="Upload product images" />
      </div>

      <div className="acard">
        <div className="between" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Colours & per-colour images</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={addColor}><FiPlus /> Add colour</button>
        </div>
        <div className="color-editor">
          {form.colors.length === 0 && <p className="muted">No colours added. Optional — add variants like "Black", "White", etc.</p>}
          {form.colors.map((c, i) => (
            <div className="color-row" key={i}>
              <input type="color" value={c.hex} onChange={(e) => setColor(i, 'hex', e.target.value)} />
              <input className="input" style={{ maxWidth: 200 }} placeholder="Colour name" value={c.name} onChange={(e) => setColor(i, 'name', e.target.value)} />
              <div style={{ flex: 1, minWidth: 220 }}>
                <ImageUploader value={c.images} onChange={(v) => setColor(i, 'images', v)} label={`Images for ${c.name || 'this colour'}`} />
              </div>
              <button type="button" className="icon-btn danger" onClick={() => removeColor(i)}><FiTrash2 /></button>
            </div>
          ))}
        </div>
      </div>

      <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Product'}</button>
      <ToastHost />
    </form>
  );
}
